import './menu-item-cart-card.css';

import { Util } from '../../../utils/util';
import { IName } from '../../../interfaces/global.interfaces';
import { IOrderItem } from '../../../models/order.interface';
import { OnlineOrderingUtil } from '../../../online-ordering/online-ordering-util';
import { IDataItem } from '../../../models/data.interface';
import { IMenuItemCartCardUiParams, MenuItemCartCardBase } from './menu-item-cart-card-base';

/**
 * Class representing a cart card for menu items in an order.
 * Responsible for rendering and managing menu item cards in the cart.
 */
export class MenuItemCartCardSingleMode extends MenuItemCartCardBase {
    constructor(params: IMenuItemCartCardUiParams) {
        super(params);
    }

    protected override _afterCardRender(
        _item: IOrderItem,
        card: HTMLElement
    ): void {
        const modifiers = Array.from(
            card.querySelectorAll(".menu-item-cart-card-modifiers")
        ) as HTMLElement[];

        if(this._state.keepExpanded) {
            modifiers.forEach(m => m.classList.add("expanded"));
            return;
        }

        modifiers.forEach(m => {
            m.onclick = () => m.classList.toggle("expanded");
        });
    }

    /**
     * Generates the HTML for a menu item card in the cart.
     * @protected
     * @param {IOrderItem[]} items - The group of items to display in the card.
     * @param {boolean} showOptions - Indicates whether to include customization options in the card.
     * @returns {HTMLElement | null} - The generated HTML element for the item card, or null if the item is invalid.
     */
    protected _generateItemCardHtml(items: IOrderItem[]): HTMLElement | null {
        const firstHalfItem = items[0];
        const menuItem = this._data.getProperty("Items").find(x => x.ItemId === firstHalfItem.ItemId);
        if (!menuItem)
            return null;

        // 2DO: Comment
        //if (oItem.Comment !== "" && (oItem.HalfCount === 1 || (oItem.HalfCount === 2 && oItem.HalfIndex === 2)))
        //    divItem.innerHTML += `<div class="cart-item-row">${oItem.Comment}</div>`;

        const isHalfHalf = firstHalfItem.HalfCount > 1;

        const header = this._generateHeaderTemplate(items, menuItem);
        const itemPoints = isHalfHalf ? this._generateItemPointsTemplate(items) : "";
        const itemsTemplate = this._generateItemsTemplate(items);
        const specialInstructions = this._generateInstructionTemplate(firstHalfItem);
        const taxes = this._generateTaxesTemplate(firstHalfItem);
        const footer = this._state.showOptions ? this.generateOptionsTemplate(firstHalfItem) : this._generateItemQuantityTemplate(firstHalfItem);

        const html = `
            <article class="menu-item-cart-card-parent" aria-labelledby="cart-item-title-${firstHalfItem.ItemKey} cart-item-price-${firstHalfItem.ItemKey}">
                ${header}
                ${itemPoints}
                ${itemsTemplate}
                ${specialInstructions}
                ${taxes}
                ${footer}
            </article>`;

        return Util.createHtmlElementFromTemplate(html);
    }

    protected _generateHeaderTemplate(items: IOrderItem[], menuItem: IDataItem): string {
        const firstHalfItem = items[0];
        const country = this._data.getProperty("StoreInfo").country;

        const isHalfHalf = firstHalfItem.HalfCount > 1;
        const hasImage = Boolean(menuItem.ImageURL);
        const name = this._localizationService.GetName(menuItem.Names);

        const price = this.calculateItemPrice(firstHalfItem, isHalfHalf);
        const calories = this.getItemCalories(menuItem, firstHalfItem);

        let sizeName: IName[] = [];
        if (firstHalfItem.SizeId > 0) {
            const iSize = OnlineOrderingUtil.GetItemSize(menuItem, this._order.getProperty("OrderTypeID"), firstHalfItem.SizeId, true);
            const size = this._data.getProperty("Sizes").find(x => x.SizeId === iSize?.SizeId) ?? null;
            if (size)
                sizeName = size.Names;
        }

        return `
                <header class="menu-item-cart-card">
                    ${hasImage ?
                        `<figure class="menu-item-cart-card-image">
                            <img src="${menuItem.ImageURL}" alt="${name}">
                        </figure>` : "" 
                    }
                    <div class="menu-item-cart-card-details">
                        <h3 id="cart-item-title-${firstHalfItem.ItemKey}" class="item-title">${name}</h3>
                        <p class="item-description">
                            <span class="sr-only">${this._localizationService.names("Size")}:</span>
                            ${this._localizationService.GetName(sizeName)}
                        </p>
                        <p class="item-calories">
                            <span class="sr-only">${this._localizationService.names("CALORIES")}:</span>
                            ${this._localizationService.GetName(calories)}
                        </p>
                    </div>
                    <div class="menu-item-cart-card-price">
                        <p id="cart-item-price-${firstHalfItem.ItemKey}">
                            <span class="sr-only">${this._localizationService.names("Price")}:</span>
                            ${Util.formatMoney(price * firstHalfItem.Quantity, country)}
                        </p>
                    </div>
                </header>`
    }

    protected _generateItemPointsTemplate(items: IOrderItem[]): string {
        return "";

        // 2DO: Points
        // let displayPoints = false;
        // const loyaltyPoints = displayPoints ? OnlineOrderingUtil.GetLoyaltyPoints(firstHalfItem.ItemId, firstHalfItem.SizeId, this._data.getBrandProperty("LoyaltyItems")) || 0 : 0;
        //if (this._loyaltyProvider.hasPointRedemption() && this._user.getProperty("LoyaltyData")) {
        //    const points = OnlineOrderingUtil.GetLoyaltyPoints(oItem.ItemId, oItem.SizeId, this._data.getProperty("LoyaltyItems"));
        //    if (points) {
        //        let innerText = "";
        //        const hidden = (oItem.IsRedeemed || points > (this._user.getProperty("LoyaltyData")?.CurrentPoints || 0));
        //        if (!hidden) {
        //            if (oItem.Quantity > 1)
        //                innerText = (points) ? `<span ltag="Redeem">${Names("Redeem")}</span>&nbsp1&nbsp<span ltag="For">${Names("For")}</span>&nbsp${points}&nbsp<span ltag="Points">${Names("Points")}</span>` : "";
        //            else
        //                innerText = (points) ? `<span ltag="RedeemFor">${Names("RedeemFor")}</span>&nbsp${points}&nbsp<span ltag="Points">${Names("Points")}</span>` : "";

        //            const btnPnts =
        //                `<button id="btn_cart_item_redeem_${oItem.ItemKey}" name="btn_cart_item_redeem" class="${this._context.getProperty("buttonHoverStyle")} cart-item-redeem-btn" data-pts="${points}" data-iky="${oItem.ItemKey}">${innerText}</button>`;

        //            divItem.innerHTML += btnPnts;
        //        }
        //    }
        //}
        //}

        // 2DO: Point stuff
        //if (displayPoints && (oItem.HalfCount === 1 || (oItem.HalfCount === 2 && oItem.HalfIndex === 2)))
        //    divItem.innerHTML += `<div style="text-align: center;">${loyaltyPoint} Points required</div>`;

    }

    private _generateItemsTemplate(items: IOrderItem[]): string {
        const firstHalfItem = items[0];
        const isHalfHalf = firstHalfItem.HalfCount > 1;

        const modifiersText = OnlineOrderingUtil.generateModifiersText(
            isHalfHalf,
            OnlineOrderingUtil._collectModifiersForDisplay(items),
            this._localizationService,
            this._data);

        return `
            <section class="menu-item-cart-card-modifier-group" aria-labelledby="cart-item-modifiers-${firstHalfItem.ItemKey}">
                <h4 id="cart-item-modifiers-${firstHalfItem.ItemKey}" class="sr-only">
                    ${this._localizationService.names("SELECTED_MODIFIERS")}
                </h4>
                ${modifiersText}
            </section>`;
    }

    protected _generateInstructionTemplate(item: IOrderItem): string {
        const instructions = this._generateInstructionList(item);
        if (instructions.length === 0)
            return "";

        return `
            <section class="menu-item-cart-card-instructions" aria-labelledby="instructions-label-${item.ItemKey}">
                <span id="instructions-label-${item.ItemKey}">${this._localizationService.names("SpecialInstructions")}:</span>
                <ul class="instructions-list">
                    ${instructions.join("")}
                </ul>
            </section>`;
    }

    /**
     * Generates the HTML for special instructions for the item.
     * @protected
     * @param {IOrderItem} item - The specific order item.
     * @returns {string} - The HTML for special instructions.
     */
    protected _generateInstructionList(item: IOrderItem): string[] {
        if (!item.Instructions || item.Instructions.length === 0)
            return [];

        const instructions: string[] = [];
        for (const instruction of item.Instructions) {
            const instructionData = this._data.getProperty("Instructions").find((x) => x.CommentId === instruction.commentId);
            if (instructionData) {
                const name = this._localizationService.GetNewName(instructionData.Names);
                instructions.push(`<li>${name}</li>`);
            }
        }

        return instructions;
    }

    /**
     * Generates the HTML for taxes displayed next to the item.
     * @protected
     * @param {IOrderItem} item - The specific order item.
     * @returns {string} - The HTML for taxes.
     */
    protected _generateTaxesTemplate(item: IOrderItem): string {
        let taxesHtml = "";

        for (const tax of item.Taxes.filter(x => x.DisplayNextToItem)) {
            const taxData = this._data.getProperty("Taxes").find(x => x.TaxId === tax.TaxId);
            if (taxData?.OrderTypesExcluded.includes(this._order.getProperty("OrderTypeID")))
                continue;

            if (!tax.TaxTypeName)
                continue;

            const taxName = typeof tax.TaxTypeName === "string" ? JSON.parse(tax.TaxTypeName) as IName[] : tax.TaxTypeName;
            const localizedTaxNames = this.localizeTaxNames(taxName, tax.Amount);

            taxesHtml += `<p class="item-tax" ltagj="${Util.toLtagj(localizedTaxNames)}">${this._localizationService.GetName(localizedTaxNames)}</p>`;
        }

        return taxesHtml;
    }
}